import PropTypes from "prop-types";
import React, { memo, useCallback, useEffect, useMemo, useState } from "react";
import { BettoPrimaryButton } from "../BettoButton";
import { FormActions, FormField, FormInput, FormLabel, FormRoot } from "./elements";

const normalizeEmail = (value) => (value ?? "").toString().trim();

export const BettoAccountDetailsForm = memo(({ userInfo, translate, onSave, updateProfile }) => {
	const hasNationalId = window.PROFILE_HAS_NATIONAL_ID;
	const [phone, setPhone] = useState(userInfo?.phone_number || "");
	const [email, setEmail] = useState(userInfo?.email || "");
	const [nationalId, setNationalId] = useState(userInfo?.national_id || "");

	const isNationalIdEmpty = hasNationalId && !userInfo?.national_id;
	const canEditPhone = Boolean(updateProfile);
	const canEditEmail = !userInfo?.email;

	const isEmailChanged = useMemo(
		() => normalizeEmail(email) !== normalizeEmail(userInfo?.email),
		[email, userInfo?.email]
	);

	useEffect(() => {
		setPhone(userInfo?.phone_number || "");
		setEmail(userInfo?.email || "");
		setNationalId(userInfo?.national_id || "");
	}, [userInfo, updateProfile]);

	const showSaveButton = canEditPhone || canEditEmail || isEmailChanged || isNationalIdEmpty;

	const handleSave = useCallback(() => {
		const attributes = [];

		if (isEmailChanged || canEditEmail) {
			attributes.push({
				Name: "email",
				Value: normalizeEmail(email),
			});
		}

		if (hasNationalId && nationalId && isNationalIdEmpty) {
			attributes.push({
				Name: "custom:nationalId",
				Value: nationalId,
			});
		}

		if (updateProfile && phone) {
			attributes.push({
				Name: "phone_number",
				Value: phone,
			});
		}

		if (attributes.length === 0) {
			return;
		}

		onSave({ UserAttributes: attributes });
	}, [
		canEditEmail,
		email,
		hasNationalId,
		isEmailChanged,
		isNationalIdEmpty,
		nationalId,
		onSave,
		phone,
		updateProfile,
	]);

	const hasEditableFields = canEditPhone || canEditEmail || isNationalIdEmpty;

	if (!hasEditableFields) {
		return null;
	}

	return (
		<FormRoot>
			{canEditEmail ? (
				<FormField>
					<FormLabel>{translate("Email")}</FormLabel>
					<FormInput
						type="email"
						value={email}
						onChange={(event) => setEmail(event.target.value)}
						placeholder={translate("PLEASE_INPUT_EMAIL")}
					/>
				</FormField>
			) : null}

			{canEditPhone ? (
				<FormField>
					<FormLabel>{translate("Phone Number")}</FormLabel>
					<FormInput
						type="tel"
						value={phone}
						onChange={(event) => setPhone(event.target.value)}
						placeholder={translate("Please enter your phone number")}
					/>
				</FormField>
			) : null}

			{isNationalIdEmpty ? (
				<FormField>
					<FormLabel>{translate("National ID")}</FormLabel>
					<FormInput
						value={nationalId}
						onChange={(event) => setNationalId(event.target.value)}
						placeholder={translate("Please enter your National ID")}
					/>
				</FormField>
			) : null}

			{showSaveButton ? (
				<FormActions>
					<BettoPrimaryButton
						fullWidth
						onClick={handleSave}>
						{translate("Save")}
					</BettoPrimaryButton>
				</FormActions>
			) : null}
		</FormRoot>
	);
});

BettoAccountDetailsForm.displayName = "BettoAccountDetailsForm";

BettoAccountDetailsForm.propTypes = {
	userInfo: PropTypes.object,
	translate: PropTypes.func.isRequired,
	onSave: PropTypes.func.isRequired,
	updateProfile: PropTypes.bool,
};
